import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const faqCategories = {
  "get-started": [
    {
      question: "What is Trip Secure?",
      answer:
        "Trip Secure is a digital microinsurance platform that allows verified insurance providers to distribute per-trip, return-trip, multi-trip, and subscription-based travel insurance directly to road travelers across web, mobile app, USSD, and SMS.",
    },
    {
      question: "How do I join Trip Secure as a provider?",
      answer:
        'You can become a provider by registering on the platform. Visit the "Become a Provider" page, complete the onboarding form, and upload your company\'s regulatory documents. Once verified, your policies will be listed.',
    },
  ],
  "setting-up-policies": [
    {
      question: "What kind of insurance policies can I offer?",
      answer:
        "Trip Secure supports:\n• Single-Trip Insurance\n• Return-Trip Insurance\n• Multi-Trip Packages\n• Subscription-Based Insurance\n\nYou can define your coverage options (accident, disability, medical, luggage, etc.), pricing, validity, and refund/claim terms.",
    },
    {
      question: "Can I edit or update my policies later?",
      answer:
        "Yes. Providers can update pricing, benefits, exclusions, and claim processes as long as changes do not affect active or in-progress policies. All edits go through Trip Secure admin review.",
    },
  ],
  "managing-dashboard": [
    {
      question: "Can I track how my insurance products are performing?",
      answer:
        "Yes. Every provider gets access to a dashboard to monitor:\n• Number of active policies\n• Sales performance\n• Claims filed\n• Refunds requested\n• Regional coverage breakdown",
    },
  ],
  "claims-handling": [
    {
      question: "Who handles customer claims and support?",
      answer:
        "Trip Secure provides a shared claim submission portal. Customers can file claims through the app or USSD. The platform collects and forwards claims and documentation to your team, who will verify and process them based on your policy rules.",
    },
    {
      question: "What happens if a customer cancels a trip?",
      answer:
        "If your policy supports refunds for canceled trips, Trip Secure allows users to request refunds at least 6 hours before trip time. The platform validates and initiates refund requests based on your stated policy terms.",
    },
  ],
  "payment-settlements": [
    {
      question: "Is there a fee to list my insurance on Trip Secure?",
      answer:
        "No upfront fee is required to list your products. Trip Secure uses a commission-based model, charging a percentage per successful sale. Detailed terms are shared during onboarding.",
    },
    {
      question: "How are premiums collected and settled?",
      answer:
        "All payments are collected via mobile money or card at checkout. Trip Secure settles provider payouts directly to your registered account on a scheduled basis (e.g., weekly or bi-weekly).",
    },
  ],
  "technical-support": [
    {
      question: "Is there any API integration available?",
      answer:
        "Yes. Trip Secure supports API integrations for insurers who want real-time sync between their internal systems and our platform. API documentation is available upon request.",
    },
  ],
  "best-practices": [
    {
      question: "What are the best practices for setting up policies?",
      answer:
        "Best practices include:\n• Setting competitive pricing based on market research\n• Clearly defining coverage terms and exclusions\n• Providing comprehensive customer support\n• Regularly reviewing and updating policy performance\n• Ensuring compliance with regulatory requirements",
    },
  ],
};

export default function FAQAccordion({ selectedCategory = "get-started" }) {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const currentFaqs = faqCategories[selectedCategory] || [];

  return (
    <motion.div layout className="p-2 rounded-xl bg-white/70">
      {currentFaqs.length > 0 ? (
        currentFaqs.map((faq, idx) => {
          const isOpen = openFaqIndex === idx;
          return (
            <motion.div
              key={faq.question}
              layout
              className="border-b last:border-b-0 border-emerald-600/15"
            >
              <button
                className="flex gap-4 justify-between items-center px-3 py-3 w-full text-sm text-left text-emerald-800"
                onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                aria-expanded={isOpen}
              >
                <span>{faq.question}</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${
                    isOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      duration: 0.35,
                      ease: [0.22, 0.61, 0.36, 1],
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="px-3 pb-3 text-xs text-gray-700 whitespace-pre-line">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })
      ) : (
        <div className="px-3 py-6 text-sm text-center text-gray-500">
          No FAQs available for this category yet.
        </div>
      )}
    </motion.div>
  );
}
